import { forwardRef, useId } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import { resolveBaseAppearance } from './theme';
import type { BaseFieldsetProps } from './types';
import { cn, hasRenderableNode } from './utils';

function mergeAriaIds(...values: Array<string | undefined>) {
  const ids = values.flatMap(
    (value) => value?.split(/\s+/).filter(Boolean) ?? [],
  );
  return ids.length ? Array.from(new Set(ids)).join(' ') : undefined;
}

function resolveOptionalId(
  value: string | undefined,
  fallback: string,
  name: string,
) {
  if (value === undefined) return fallback;
  if (!value.trim() || /\s/.test(value)) {
    throw new TypeError(
      `BaseFieldset ${name} must be a non-empty ID without whitespace.`,
    );
  }
  return value;
}

export const BaseFieldset = forwardRef<HTMLFieldSetElement, BaseFieldsetProps>(
  function BaseFieldset(
    {
      children,
      legend,
      description,
      descriptionId,
      error,
      errorId,
      disabled,
      appearance,
      className,
      classNames,
      style,
      'aria-describedby': ariaDescribedBy,
      'aria-errormessage': ariaErrorMessage,
      ...fieldsetProps
    },
    ref,
  ) {
    const generatedId = useId();
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);

    if (
      !hasRenderableNode(legend) ||
      (typeof legend === 'string' && !legend.trim())
    ) {
      throw new TypeError('BaseFieldset legend must contain an accessible name.');
    }
    if (disabled !== undefined && typeof disabled !== 'boolean') {
      throw new TypeError('BaseFieldset disabled must be a boolean.');
    }

    const hasDescription = hasRenderableNode(description);
    const hasError = hasRenderableNode(error);
    const resolvedDescriptionId = hasDescription
      ? resolveOptionalId(
          descriptionId,
          `${generatedId}-description`,
          'descriptionId',
        )
      : undefined;
    const resolvedErrorId = hasError
      ? resolveOptionalId(errorId, `${generatedId}-error`, 'errorId')
      : undefined;
    return (
      <fieldset
        {...componentAttributes}
        {...fieldsetProps}
        ref={ref}
        disabled={disabled}
        aria-describedby={mergeAriaIds(ariaDescribedBy, resolvedDescriptionId)}
        aria-errormessage={mergeAriaIds(ariaErrorMessage, resolvedErrorId)}
        data-monkeys-component="fieldset"
        data-slot="root"
        className={cn(theme.slots.fieldsetRoot, classNames?.root, className)}
        style={{
          ...theme.styles.fieldset,
          ...style,
        }}
      >
        <legend
          data-slot="legend"
          className={cn(theme.slots.fieldsetLegend, classNames?.legend)}
          style={theme.styles.fieldsetLegend}
        >
          {legend}
        </legend>
        {hasDescription ? (
          <div
            id={resolvedDescriptionId}
            data-slot="description"
            className={cn(
              theme.slots.fieldsetDescription,
              classNames?.description,
            )}
            style={theme.styles.fieldsetDescription}
          >
            {description}
          </div>
        ) : null}
        {hasError ? (
          <div
            id={resolvedErrorId}
            data-slot="error"
            className={cn(theme.slots.fieldsetError, classNames?.error)}
            style={theme.styles.fieldsetError}
          >
            {error}
          </div>
        ) : null}
        <div
          data-slot="content"
          className={cn(theme.slots.fieldsetContent, classNames?.content)}
          style={theme.styles.fieldsetContent}
        >
          {children}
        </div>
      </fieldset>
    );
  },
);
